import React, { useState } from 'react';
import {
  Modal,
  Stack,
  TextContainer,
  Heading,
  Card,
  List,
  TextStyle,
  Badge,
  Collapsible,
  Button,
  Banner
} from '@shopify/polaris';

/**
 * Comprehensive help component for template variables in Shopify Product Editor Pro
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 */
export const TemplateVariablesHelp = ({
  open,
  onClose
}) => {
  const [expandedSections, setExpandedSections] = useState({
    productFields: true,
    variantFields: false,
    metafields: false,
    examples: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Template Variables Reference"
      large
      secondaryActions={[
        {
          content: 'Close',
          onAction: onClose
        }
      ]}
    >
      <Modal.Section>
        <Stack vertical spacing="loose">
          {/* Introduction */}
          <Banner status="info">
            <p>
              <strong>Template variables</strong> allow you to dynamically reference product data in your AI prompts. 
              Use curly braces <TextStyle variation="code">{'{variable_name}'}</TextStyle> to insert product information into your templates.
            </p>
          </Banner>

          {/* Product Fields Section */}
          <Card sectioned>
            <Stack vertical spacing="tight">
              <Button
                plain
                onClick={() => toggleSection('productFields')}
                ariaExpanded={expandedSections.productFields}
                ariaControls="product-fields-content"
              >
                <Stack alignment="center">
                  <Heading>Product Fields</Heading>
                  <Badge status="success">Core Variables</Badge>
                </Stack>
              </Button>
              
              <Collapsible
                open={expandedSections.productFields}
                id="product-fields-content"
                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
              >
                <Stack vertical spacing="tight">
                  <TextContainer>
                    <p>These variables reference the main product information:</p>
                  </TextContainer>
                  
                  <List type="bullet">
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{title}'}</code>
                        <TextStyle variation="subdued">- Product title/name</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{body_html}'}</code> or <code>{'{description}'}</code>
                        <TextStyle variation="subdued">- Product description (HTML format)</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{product_type}'}</code>
                        <TextStyle variation="subdued">- Product category/type</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{vendor}'}</code>
                        <TextStyle variation="subdued">- Brand/manufacturer name</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{tags}'}</code>
                        <TextStyle variation="subdued">- Product tags (comma-separated)</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{handle}'}</code>
                        <TextStyle variation="subdued">- URL handle/slug</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{inventory}'}</code>
                        <TextStyle variation="subdued">- Total inventory quantity</TextStyle>
                      </Stack>
                    </List.Item>
                  </List>
                  
                  <TextContainer>
                    <p><strong>Product Options:</strong></p>
                  </TextContainer>
                  
                  <List type="bullet">
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{option1_name}'}</code>
                        <TextStyle variation="subdued">- First option name (e.g., "Size", "Color")</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{option1_values}'}</code>
                        <TextStyle variation="subdued">- First option values (e.g., ["Small", "Medium", "Large"])</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{option2_name}'}</code>
                        <TextStyle variation="subdued">- Second option name (e.g., "Color", "Material")</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{option2_values}'}</code>
                        <TextStyle variation="subdued">- Second option values (e.g., ["Red", "Blue", "Green"])</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{option3_name}'}</code>
                        <TextStyle variation="subdued">- Third option name (e.g., "Style", "Fit")</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{option3_values}'}</code>
                        <TextStyle variation="subdued">- Third option values (e.g., ["Regular", "Slim", "Loose"])</TextStyle>
                      </Stack>
                    </List.Item>
                  </List>
                </Stack>
              </Collapsible>
            </Stack>
          </Card>

          {/* Variant Fields Section */}
          <Card sectioned>
            <Stack vertical spacing="tight">
              <Button
                plain
                onClick={() => toggleSection('variantFields')}
                ariaExpanded={expandedSections.variantFields}
                ariaControls="variant-fields-content"
              >
                <Stack alignment="center">
                  <Heading>Variant Fields</Heading>
                  <Badge status="attention">Advanced Variables</Badge>
                </Stack>
              </Button>
              
              <Collapsible
                open={expandedSections.variantFields}
                id="variant-fields-content"
                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
              >
                <Stack vertical spacing="tight">
                  <TextContainer>
                    <p>These variables reference product variant information:</p>
                  </TextContainer>
                  
                  <List type="bullet">
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{variants.sku}'}</code>
                        <TextStyle variation="subdued">- SKU codes for all variants</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{variants.price}'}</code>
                        <TextStyle variation="subdued">- Variant prices</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{variants.compare_at_price}'}</code>
                        <TextStyle variation="subdued">- Compare-at prices (MSRP)</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{variants.inventory_quantity}'}</code>
                        <TextStyle variation="subdued">- Inventory quantities per variant</TextStyle>
                      </Stack>
                    </List.Item>
                  </List>
                  
                  <Banner status="warning">
                    <p>
                      <strong>Note:</strong> Variant fields contain data for all variants. 
                      The AI will process this information appropriately for your use case.
                    </p>
                  </Banner>
                </Stack>
              </Collapsible>
            </Stack>
          </Card>

          {/* Metafields Section */}
          <Card sectioned>
            <Stack vertical spacing="tight">
              <Button
                plain
                onClick={() => toggleSection('metafields')}
                ariaExpanded={expandedSections.metafields}
                ariaControls="metafields-content"
              >
                <Stack alignment="center">
                  <Heading>Metafields</Heading>
                  <Badge status="info">Custom Data</Badge>
                </Stack>
              </Button>
              
              <Collapsible
                open={expandedSections.metafields}
                id="metafields-content"
                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
              >
                <Stack vertical spacing="tight">
                  <TextContainer>
                    <p>
                      Metafields store custom data for your products. Use the format:
                    </p>
                  </TextContainer>
                  
                  <Card sectioned subdued>
                    <code>{'{metafield.namespace.key}'}</code>
                  </Card>
                  
                  <TextContainer>
                    <p><strong>Common metafield examples:</strong></p>
                  </TextContainer>
                  
                  <List type="bullet">
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{metafield.seo.keywords}'}</code>
                        <TextStyle variation="subdued">- SEO keywords</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{metafield.custom.material}'}</code>
                        <TextStyle variation="subdued">- Product material</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{metafield.specifications.dimensions}'}</code>
                        <TextStyle variation="subdued">- Product dimensions</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{metafield.custom.care_instructions}'}</code>
                        <TextStyle variation="subdued">- Care instructions</TextStyle>
                      </Stack>
                    </List.Item>
                    <List.Item>
                      <Stack alignment="center">
                        <code>{'{metafield.shopify.weight}'}</code>
                        <TextStyle variation="subdued">- Product weight</TextStyle>
                      </Stack>
                    </List.Item>
                  </List>
                  
                  <Banner status="info">
                    <p>
                      <strong>Tip:</strong> You can add metafields to your template by using the "Add Metafield" 
                      button in the template editor. The system will automatically detect available metafields.
                    </p>
                  </Banner>
                </Stack>
              </Collapsible>
            </Stack>
          </Card>

          {/* Examples Section */}
          <Card sectioned>
            <Stack vertical spacing="tight">
              <Button
                plain
                onClick={() => toggleSection('examples')}
                ariaExpanded={expandedSections.examples}
                ariaControls="examples-content"
              >
                <Stack alignment="center">
                  <Heading>Template Examples</Heading>
                  <Badge>Practical Usage</Badge>
                </Stack>
              </Button>
              
              <Collapsible
                open={expandedSections.examples}
                id="examples-content"
                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
              >
                <Stack vertical spacing="loose">
                  <TextContainer>
                    <p><strong>Example 1: SEO Product Description</strong></p>
                  </TextContainer>
                  <Card sectioned subdued>
                    <pre><code>
Create an SEO-optimized product description for "{'{title}'}" that incorporates 
the keywords "{'{metafield.seo.keywords}'}". The product type is {'{product_type}'} 
and it's made by {'{vendor}'}. Current description: {'{description}'}
                    </code></pre>
                  </Card>

                  <TextContainer>
                    <p><strong>Example 2: Product Title Generation</strong></p>
                  </TextContainer>
                  <Card sectioned subdued>
                    <pre><code>
Generate a compelling product title for this {'{product_type}'} from {'{vendor}'}. 
Include relevant keywords from: {'{metafield.seo.keywords}'}. 
Current title: "{'{title}'}". Tags: {'{tags}'}
                    </code></pre>
                  </Card>

                  <TextContainer>
                    <p><strong>Example 3: Product Specifications</strong></p>
                  </TextContainer>
                  <Card sectioned subdued>
                    <pre><code>
Extract and format product specifications for "{'{title}'}". 
Material: {'{metafield.custom.material}'}, 
Dimensions: {'{metafield.specifications.dimensions}'}, 
Care: {'{metafield.custom.care_instructions}'}. 
Format as bullet points.
                    </code></pre>
                  </Card>

                  <TextContainer>
                    <p><strong>Example 4: Tag Generation</strong></p>
                  </TextContainer>
                  <Card sectioned subdued>
                    <pre><code>
Generate relevant product tags for "{'{title}'}". 
Product type: {'{product_type}'}, Vendor: {'{vendor}'}, 
Current tags: {'{tags}'}, Description: {'{description}'}. 
Return as comma-separated list.
                    </code></pre>
                  </Card>
                </Stack>
              </Collapsible>
            </Stack>
          </Card>

          {/* Best Practices */}
          <Card sectioned>
            <TextContainer>
              <Heading>Best Practices</Heading>
              <List type="bullet">
                <List.Item>
                  <TextStyle variation="strong">Always use curly braces:</TextStyle> Variables must be wrapped in {'{}'} to be recognized
                </List.Item>
                <List.Item>
                  <TextStyle variation="strong">Case sensitive:</TextStyle> Variable names are case-sensitive (use {'{title}'} not {'{Title}'})
                </List.Item>
                <List.Item>
                  <TextStyle variation="strong">Test with sample data:</TextStyle> Preview your templates with real products before bulk execution
                </List.Item>
                <List.Item>
                  <TextStyle variation="strong">Handle missing data:</TextStyle> Not all products have all metafields - AI will handle missing values gracefully
                </List.Item>
                <List.Item>
                  <TextStyle variation="strong">Be specific:</TextStyle> Provide clear instructions on how to use the variable data in your prompts
                </List.Item>
              </List>
            </TextContainer>
          </Card>
        </Stack>
      </Modal.Section>
    </Modal>
  );
};

export default TemplateVariablesHelp;
